import type React from 'react';
import type {Page} from '@PosterWhiteboard/page/page.class';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import type {SlideItem} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.types';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {ColorTypes} from '@Components/controlled-list/components/controlled-list-item-type-7';
import {AlignType} from '@PosterWhiteboard/page/alignment.class';
import {ReplaceMediaControlsButtons} from '@Components/replace-media-controls';
import {isUserPremium} from '@Libraries/user.library';
import {updateIsSpellCheckEnabledForUser} from '@Libraries/spell-check-library';
import {deleteItemByID, onCopyStylesClicked} from '@Components/poster-editor/library/poster-editor-library';
import {onOpenResizeModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {makeShortcut} from '@Utils/shortcut.util';
import {PosterGestures} from '@PosterWhiteboard/poster/poster-gestures.class';
import type {SelectedTimelineItem} from '@Components/poster-editor/components/timeline/timeline.types';
import {isTransparentColorBackground} from '@PosterWhiteboard/page/background/background.types';

/**
 * Shared state and callbacks passed to every menu builder.
 * Keeps builders as pure data functions — they don't reach into React.
 */
export interface PopupMenuCtx {
  currentPage: Page | undefined;
  activeItem: ItemType | null;
  activeSlide: SlideItem | null;
  isMultiSelectEnabled: boolean | undefined;
  isSpellCheckEnabled: boolean;
  close: () => void;
  // Callbacks owned by the parent (need component state / hooks).
  onCopySelection: (e?: React.MouseEvent<HTMLElement>) => void;
  pasteGraphicItems: (e?: React.MouseEvent<HTMLElement>) => Promise<void>;
  onCutFromText: () => Promise<void>;
  onCopyFromText: () => Promise<void>;
  onPasteText: (e?: React.MouseEvent<HTMLElement>) => Promise<void>;
  onSelectAllText: () => void;
  onMultiSelectClicked: () => void;
  onReplaceOption: (type: ReplaceMediaControlsButtons) => void;
  onReplaceAudioOption: (type: ReplaceMediaControlsButtons) => void;
  onReplaceBackgroundOption: (type: ReplaceMediaControlsButtons) => void;
  onSplitItem: () => void;
  selectedTimelineItem: SelectedTimelineItem | undefined;
  hasTopBlock: boolean;
}


/* -------------------------------------------------------------------------- */
/* Submenu builders                                                           */
/* -------------------------------------------------------------------------- */

export const buildPositionSubMenuItems = (ctx: PopupMenuCtx): ControlledListItemType[] => {
  const {currentPage, close} = ctx;
  return [
    {
      id: 'pos-1',
      icon: 'icon-send-to-back',
      text: window.i18next.t('pmwjs_send_backward'),
      shortcut: makeShortcut(['Shift', '↓']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      isDisabled: !currentPage?.items.canSelectedItemsBeMovedBackwards(),
      onClick: (): void => {
        currentPage?.items.sendBackwardsSelection();
        close();
      },
    },
    {
      id: 'pos-2',
      icon: 'icon-to-back',
      text: window.i18next.t('pmwjs_send_to_bottom'),
      shortcut: makeShortcut(['Ctrl', 'Shift', '↓']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      isDisabled: !currentPage?.items.canSelectedItemsBeMovedBackwards(),
      onClick: (): void => {
        currentPage?.items.sendToBackSelection();
        close();
      },
    },
    {
      id: 'pos-3',
      icon: 'icon-send-to-front',
      text: window.i18next.t('pmwjs_bring_forward'),
      shortcut: makeShortcut(['Shift', '↑']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      isDisabled: !currentPage?.items.canSelectedItemsBeMovedForward(),
      onClick: (): void => {
        currentPage?.items.bringForwardSelection();
        close();
      },
    },
    {
      id: 'pos-4',
      icon: 'icon-to-front',
      text: window.i18next.t('pmwjs_bring_to_top'),
      shortcut: makeShortcut(['Ctrl', 'Shift', '↑']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      isDisabled: !currentPage?.items.canSelectedItemsBeMovedForward(),
      onClick: (): void => {
        currentPage?.items.bringToFrontSelection();
        close();
      },
    },
  ];
};

export const buildReplaceSubMenuItems = (ctx: PopupMenuCtx): ControlledListItemType[] => {
  const {onReplaceOption} = ctx;
  return [
    {
      id: 'replace-uploads',
      icon: 'icon-upload',
      text: window.i18next.t('pmwjs_uploads'),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        onReplaceOption(ReplaceMediaControlsButtons.MY_UPLOADS);
      },
    },
    {
      id: 'replace-media',
      icon: 'icon-media',
      text: window.i18next.t('pmwjs_explore_media'),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        onReplaceOption(ReplaceMediaControlsButtons.EXPLORE_MEDIA);
      },
    },
  ];
};

export const buildBackgroundReplaceSubMenuItems = (ctx: PopupMenuCtx): ControlledListItemType[] => {
  const {onReplaceBackgroundOption} = ctx;
  return [
    {
      id: 'replace-bg-uploads',
      icon: 'icon-upload',
      text: window.i18next.t('pmwjs_uploads'),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        onReplaceBackgroundOption(ReplaceMediaControlsButtons.MY_UPLOADS);
      },
    },
    {
      id: 'replace-bg-media',
      icon: 'icon-media',
      text: window.i18next.t('pmwjs_explore_media'),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        onReplaceBackgroundOption(ReplaceMediaControlsButtons.EXPLORE_MEDIA);
      },
    },
  ];
};

export const buildAudioReplaceSubMenuItems = (ctx: PopupMenuCtx): ControlledListItemType[] => {
  const {onReplaceAudioOption} = ctx;
  return [
    {
      id: 'replace-audio-uploads',
      icon: 'icon-upload',
      text: window.i18next.t('pmwjs_uploads'),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        onReplaceAudioOption(ReplaceMediaControlsButtons.MY_UPLOADS);
      },
    },
    {
      id: 'replace-audio-media',
      icon: 'icon-media',
      text: window.i18next.t('pmwjs_explore_media'),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        onReplaceAudioOption(ReplaceMediaControlsButtons.EXPLORE_MEDIA);
      },
    },
  ];
};

export const buildAlignSubMenuItems = (ctx: PopupMenuCtx): ControlledListItemType[] => {
  const {currentPage, close} = ctx;
  const alignBy = (type: AlignType) => (): void => {
    currentPage?.activeSelection?.alignment?.alignItems(type);
    close();
  };
  return [
    {id: 'align-left', icon: 'icon-left-align-on-canvas', text: window.i18next.t('pmwjs_left'), type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7, isSelected: false, onClick: alignBy(AlignType.LEFT)},
    {
      id: 'align-horizontal',
      icon: 'icon-center-h-align-on-canvas',
      text: window.i18next.t('pmwjs_center'),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: alignBy(AlignType.HORIZONTAL),
    },
    {id: 'align-right', icon: 'icon-right-align-on-canvas', text: window.i18next.t('pmwjs_right'), type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7, isSelected: false, onClick: alignBy(AlignType.RIGHT)},
    {id: 'align-divider-1', type: CONTROLLED_LIST_ITEM_TYPE.DIVIDER},
    {id: 'align-top', icon: 'icon-top-align-on-canvas', text: window.i18next.t('pmwjs_top'), type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7, isSelected: false, onClick: alignBy(AlignType.TOP)},
    {
      id: 'align-vertical',
      icon: 'icon-center-v-align-on-canvas',
      text: window.i18next.t('pmwjs_middle'),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: alignBy(AlignType.VERTICAL),
    },
    {id: 'align-bottom', icon: 'icon-bottom-align-on-canvas', text: window.i18next.t('pmwjs_bottom'), type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7, isSelected: false, onClick: alignBy(AlignType.BOTTOM)},
    {id: 'align-divider-2', type: CONTROLLED_LIST_ITEM_TYPE.DIVIDER},
    {id: 'align-center', icon: 'icon-core-on-canvas', text: window.i18next.t('pmwjs_core'), type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7, isSelected: false, onClick: alignBy(AlignType.CENTER)},
  ];
};

/* -------------------------------------------------------------------------- */
/* Main popup content builders                                                */
/* -------------------------------------------------------------------------- */

const toggleSpellCheckItem = (ctx: PopupMenuCtx, id: string, withPremiumCrown: boolean): ControlledListItemType => {
  const {isSpellCheckEnabled, close} = ctx;
  return {
    id,
    icon: isSpellCheckEnabled ? 'icon-spellcheck-off' : 'icon-spellcheck',
    text: isSpellCheckEnabled ? window.i18next.t('pmwjs_disable_spellcheck') : window.i18next.t('pmwjs_enable_spellcheck'),
    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
    isSelected: false,
    rightIcon: withPremiumCrown && !isUserPremium() ? 'icon-crown' : '',
    rightIconClassName: withPremiumCrown ? 'color-premium' : undefined,
    onClick: (): void => {
      if (isUserPremium()) {
        void updateIsSpellCheckEnabledForUser(!isSpellCheckEnabled);
      } else {
        void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_SPELL_CHECK as string);
      }
      close();
    },
  };
};

const multiSelectItem = (ctx: PopupMenuCtx, id: string): ControlledListItemType => {
  return {
    id,
    icon: 'icon-select-all',
    text: window.i18next.t('pmwjs_multi_select'),
    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
    isSelected: !!ctx.isMultiSelectEnabled,
    className: PosterGestures.shouldEnableGestures() ? '' : '_hidden',
    onClick: (): void => {
      ctx.onMultiSelectClicked();
      ctx.close();
    },
  };
};

export const buildItemsForTextEdit = (ctx: PopupMenuCtx, textHasNoSelection: boolean): ControlledListItemType[] => {
  const {close, onCutFromText, onCopyFromText, onPasteText, onSelectAllText} = ctx;
  return [
    {
      id: 'text-cut',
      icon: 'icon-cut',
      text: window.i18next.t('pmwjs_cut'),
      shortcut: makeShortcut(['Ctrl', 'X']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      isDisabled: textHasNoSelection,
      onClick: (): void => {
        void onCutFromText();
        close();
      },
    },
    {
      id: 'text-copy',
      icon: 'icon-copy',
      text: window.i18next.t('pmwjs_copy_text'),
      shortcut: makeShortcut(['Ctrl', 'C']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      isDisabled: textHasNoSelection,
      onClick: (): void => {
        void onCopyFromText();
        close();
      },
    },
    {
      id: 'text-paste',
      icon: 'icon-poster-print',
      text: window.i18next.t('pmwjs_paste'),
      shortcut: makeShortcut(['Ctrl', 'V']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (_id, e): void => {
        void onPasteText(e);
        close();
      },
    },
    {
      id: 'text-select-all',
      icon: 'icon-select-all',
      text: window.i18next.t('pmwjs_select_all'),
      shortcut: makeShortcut(['Ctrl', 'A']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        onSelectAllText();
        close();
      },
    },
    {id: 'text-divider', type: CONTROLLED_LIST_ITEM_TYPE.DIVIDER},
    toggleSpellCheckItem(ctx, 'text-spellcheck', false),
  ];
};

export const buildItemsForGroup = (ctx: PopupMenuCtx): ControlledListItemType[] => {
  const {currentPage, close, onCopySelection, pasteGraphicItems} = ctx;
  const selectedGroupId = currentPage?.items.groups.getGroupIdForCurrentSelection();
  const items: ControlledListItemType[] = [
    // Separates the Position / Align submenus (rendered above the ControlledList) from the action items below.
    {id: 'group-top-divider', type: CONTROLLED_LIST_ITEM_TYPE.DIVIDER},
  ];

  if (selectedGroupId) {
    items.push({
      id: 'group-ungroup',
      icon: 'icon-ungroup',
      text: window.i18next.t('pmwjs_ungroup'),
      shortcut: makeShortcut(['Ctrl', 'Shift', 'G']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        currentPage?.items.groups.ungroupSelection();
        close();
      },
    });
  } else if (currentPage?.items.groups.isCurrentSelectionGroupable()) {
    items.push({
      id: 'group-group',
      icon: 'icon-group-item',
      text: window.i18next.t('pmwjs_group'),
      shortcut: makeShortcut(['Ctrl', 'G']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        currentPage?.items.groups.groupSelection();
        close();
      },
    });
  }

  items.push(
    {
      id: 'group-copy',
      icon: 'icon-copy',
      text: window.i18next.t('pmwjs_copy'),
      shortcut: makeShortcut(['Ctrl', 'C']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (_id, e): void => {
        onCopySelection(e);
        close();
      },
    },
    {
      id: 'group-paste',
      icon: 'icon-poster-print',
      text: window.i18next.t('pmwjs_paste'),
      shortcut: makeShortcut(['Ctrl', 'V']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (_id, e): void => {
        void pasteGraphicItems(e);
        close();
      },
    },
    {
      id: 'group-duplicate',
      icon: 'icon-copy',
      text: window.i18next.t('pmwjs_duplicate'),
      shortcut: makeShortcut(['Ctrl', 'D']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        currentPage?.activeSelection?.onDuplicate();
        close();
      },
    },
    {
      id: 'group-delete',
      icon: 'icon-delete',
      color: ColorTypes.danger,
      text: window.i18next.t('pmwjs_delete'),
      shortcut: makeShortcut(['Del']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        // The poster-level delete matches the Del shortcut this row advertises: a mixed
        // timeline selection deletes its audio half too, not just the canvas items.
        currentPage?.poster.deleteSelection();
        close();
      },
    },
    {id: 'group-bottom-divider', type: CONTROLLED_LIST_ITEM_TYPE.DIVIDER, className: PosterGestures.shouldEnableGestures() ? '' : '_hidden'},
    multiSelectItem(ctx, 'group-multi-select')
  );

  return items;
};

export const buildItemsForCanvas = (ctx: PopupMenuCtx): ControlledListItemType[] => {
  const {close, pasteGraphicItems} = ctx;
  const items: ControlledListItemType[] = [
    {
      id: 'canvas-paste',
      icon: 'icon-poster-print',
      text: window.i18next.t('pmwjs_paste'),
      shortcut: makeShortcut(['Ctrl', 'V']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (_id, e): void => {
        void pasteGraphicItems(e);
        close();
      },
    },
    {
      id: 'canvas-resize',
      icon: 'icon-resize',
      text: window.i18next.t('pmwjs_resize'),
      shortcut: makeShortcut(['Shift', 'R']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        onOpenResizeModal();
        close();
      },
    },
    multiSelectItem(ctx, 'canvas-multi-select'),
  ];
  return items;
};

export const buildItemsForTimeline = (ctx: PopupMenuCtx): ControlledListItemType[] => {
  const {close, pasteGraphicItems} = ctx;
  return [
    {
      id: 'timeline-paste',
      icon: 'icon-poster-print',
      text: window.i18next.t('pmwjs_paste'),
      shortcut: makeShortcut(['Ctrl', 'V']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (_id, e): void => {
        void pasteGraphicItems(e);
        close();
      },
    },
  ];
};

export const buildItemsForAudioItem = (ctx: PopupMenuCtx): ControlledListItemType[] => {
  const {currentPage, close, selectedTimelineItem, pasteGraphicItems} = ctx;
  const audioItemId = selectedTimelineItem?.id;
  return [
    {id: 'audio-top-divider', type: CONTROLLED_LIST_ITEM_TYPE.DIVIDER},
    {
      id: 'audio-copy',
      icon: 'icon-copy',
      text: window.i18next.t('pmwjs_copy'),
      shortcut: makeShortcut(['Ctrl', 'C']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        if (currentPage) {
          void currentPage.clipboard.checkAndCopyAudioSelection();
        }
        close();
      },
    },
    {
      id: 'audio-paste',
      icon: 'icon-poster-print',
      text: window.i18next.t('pmwjs_paste'),
      shortcut: makeShortcut(['Ctrl', 'V']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (_id, e): void => {
        void pasteGraphicItems(e);
        close();
      },
    },
    {
      id: 'audio-duplicate',
      icon: 'icon-copy',
      text: window.i18next.t('pmwjs_duplicate'),
      shortcut: makeShortcut(['Ctrl', 'D']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        if (audioItemId) {
          currentPage?.audioClips.duplicateAudio(audioItemId);
        }
        close();
      },
    },
    {
      id: 'audio-delete',
      color: ColorTypes.danger,
      icon: 'icon-delete',
      text: window.i18next.t('pmwjs_delete'),
      shortcut: makeShortcut(['Del']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        currentPage?.audioClips.deleteSelectedAudioItem();
        close();
      },
    },
  ];
};

/**
 * Menu for an audio bar right-clicked while part of a multi/mixed timeline selection.
 * Selection-wide actions only — per-clip actions (trim, replace, duplicate) need a
 * single selected clip and live in buildItemsForAudioItem.
 */
export const buildItemsForTimelineGroupSelection = (ctx: PopupMenuCtx): ControlledListItemType[] => {
  const {currentPage, close} = ctx;
  return [
    {
      id: 'timeline-selection-delete',
      color: ColorTypes.danger,
      icon: 'icon-delete',
      text: window.i18next.t('pmwjs_delete'),
      shortcut: makeShortcut(['Del']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        currentPage?.poster.deleteSelection();
        close();
      },
    },
  ];
};

export const buildItemsForBackgroundItem = (ctx: PopupMenuCtx): ControlledListItemType[] => {
  const {currentPage, close, selectedTimelineItem, hasTopBlock, pasteGraphicItems} = ctx;
  const backgroundUid = selectedTimelineItem?.id;
  const background = backgroundUid ? currentPage?.items.itemsHashMap[backgroundUid] : undefined;
  const items: ControlledListItemType[] = [];
  if (hasTopBlock) {
    items.push({id: 'background-top-divider', type: CONTROLLED_LIST_ITEM_TYPE.DIVIDER});
  }
  items.push({
    id: 'background-copy',
    icon: 'icon-copy',
    text: window.i18next.t('pmwjs_copy'),
    shortcut: makeShortcut(['Ctrl', 'C']),
    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
    isSelected: false,
    onClick: (): void => {
      if (backgroundUid && currentPage) {
        void currentPage.clipboard.copyBackground(backgroundUid);
      }
      close();
    },
  });
  if (background && !isTransparentColorBackground(background)) {
    items.push({
      id: 'background-copy-style',
      icon: 'icon-copy-style-brush',
      text: window.i18next.t('pmwjs_copy_style'),
      shortcut: makeShortcut(['Alt', 'Ctrl', 'C']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        onCopyStylesClicked();
        close();
      },
    });
  }
  items.push(
    {
      id: 'background-paste',
      icon: 'icon-poster-print',
      text: window.i18next.t('pmwjs_paste'),
      shortcut: makeShortcut(['Ctrl', 'V']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (_id, e): void => {
        void pasteGraphicItems(e);
        close();
      },
    },
    {
      id: 'background-duplicate',
      icon: 'icon-copy',
      text: window.i18next.t('pmwjs_duplicate'),
      shortcut: makeShortcut(['Ctrl', 'D']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        if (backgroundUid && currentPage) {
          void currentPage.duplicateBackgroundAndSelect(backgroundUid);
        }
        close();
      },
    },
    {
      id: 'background-delete',
      color: ColorTypes.danger,
      icon: 'icon-delete',
      text: window.i18next.t('pmwjs_delete'),
      shortcut: makeShortcut(['Del']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        if (backgroundUid) {
          deleteItemByID(backgroundUid);
        }
        close();
      },
    }
  );
  return items;
};

export const buildItemsForSingleItem = (ctx: PopupMenuCtx): ControlledListItemType[] => {
  const {currentPage, activeItem, activeSlide, close, onCopySelection, pasteGraphicItems} = ctx;
  const items: ControlledListItemType[] = [{id: 'item-top-divider', type: CONTROLLED_LIST_ITEM_TYPE.DIVIDER}];

  if (activeItem?.groupId) {
    items.push({
      id: 'item-remove-from-group',
      icon: 'icon-ungroup',
      text: window.i18next.t('pmwjs_ungroup'),
      shortcut: makeShortcut(['Ctrl', 'Shift', 'G']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        currentPage?.items.groups.ungroupSelection();
        close();
      },
    });
  }

  items.push(
    {
      id: 'item-copy',
      icon: 'icon-copy',
      text: window.i18next.t('pmwjs_copy'),
      shortcut: makeShortcut(['Ctrl', 'C']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (_id, e): void => {
        onCopySelection(e);
        close();
      },
    },
    {
      id: 'item-copy-style',
      icon: 'icon-copy-style-brush',
      text: window.i18next.t('pmwjs_copy_style'),
      shortcut: makeShortcut(['Alt', 'Ctrl', 'C']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        onCopyStylesClicked();
        close();
      },
    },
    {
      id: 'item-paste',
      icon: 'icon-poster-print',
      text: window.i18next.t('pmwjs_paste'),
      shortcut: makeShortcut(['Ctrl', 'V']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (_id, e): void => {
        void pasteGraphicItems(e);
        close();
      },
    },
    {
      id: 'item-duplicate',
      icon: 'icon-copy',
      text: window.i18next.t('pmwjs_duplicate'),
      shortcut: makeShortcut(['Ctrl', 'D']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        currentPage?.activeSelection?.onDuplicate();
        close();
      },
    },
    {
      id: 'item-lock',
      icon: activeItem?.fabricObject.lockMovementX ? 'icon-unlock' : 'icon-lock',
      text: activeItem?.fabricObject.lockMovementX ? window.i18next.t('pmwjs_unlock_item') : window.i18next.t('pmwjs_lock_item'),
      shortcut: makeShortcut(['Ctrl', 'L']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        void activeItem?.updateLock(!activeItem?.fabricObject.lockMovementX);
        close();
      },
    }
  );

  items.push(
    {
      id: 'item-delete',
      color: ColorTypes.danger,
      icon: 'icon-delete',
      text: window.i18next.t('pmwjs_delete'),
      shortcut: makeShortcut(['Del']),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      isSelected: false,
      onClick: (): void => {
        currentPage?.activeSelection?.deleteSelectedItems();
        close();
      },
    }
  );

  if (activeItem?.isText() || activeSlide?.isTextSlide()) {
    items.push(toggleSpellCheckItem(ctx, 'item-spellcheck', true));
  }

  items.push({id: 'item-bottom-divider', type: CONTROLLED_LIST_ITEM_TYPE.DIVIDER, className: PosterGestures.shouldEnableGestures() ? '' : '_hidden'}, multiSelectItem(ctx, 'item-multi-select'));

  return items;
};
